/**
 * Werkorders Lijst Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { werkordersService, WerkorderList, WerkorderStatus, WerkorderPrioriteit } from '../../services/werkorders.service';
import { useAuth } from '../../context/AuthContext';
import './Werkorders.css';

const WerkordersList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [werkorders, setWerkorders] = useState<WerkorderList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WerkorderStatus | ''>('');
  const [prioriteitFilter, setPrioriteitFilter] = useState<WerkorderPrioriteit | ''>('');
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  const loadWerkorders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await werkordersService.getAll({
        search: search || undefined,
        status: statusFilter || undefined,
        prioriteit: prioriteitFilter || undefined,
        limit: 100
      });
      setWerkorders(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de werkorders');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, prioriteitFilter]);

  useEffect(() => {
    loadWerkorders();
  }, [loadWerkorders]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!werkorders.length) return; // Wait for werkorders to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [werkorders]);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL');
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="werkorders-container">
        <div className="loading">Werkorders laden...</div>
      </div>
    );
  }

  return (
    <div className="werkorders-container">
      <div className="page-header">
        <h1>Werkorders</h1>
        {hasPermission('werkorders_aanmaken') && (
          <button className="btn btn-primary" onClick={() => navigate('/werkorders/new')}>
            + Nieuwe Werkorder
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Zoeken..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as WerkorderStatus | '')}
          className="filter-select"
        >
          <option value="">Alle statussen</option>
          <option value="Nieuw">Nieuw</option>
          <option value="Gepland">Gepland</option>
          <option value="In uitvoering">In uitvoering</option>
          <option value="Opgeschort">Opgeschort</option>
          <option value="Voltooid">Voltooid</option>
          <option value="Geannuleerd">Geannuleerd</option>
        </select>

        <select
          value={prioriteitFilter}
          onChange={(e) => setPrioriteitFilter(e.target.value as WerkorderPrioriteit | '')}
          className="filter-select"
        >
          <option value="">Alle prioriteiten</option>
          <option value="Laag">Laag</option>
          <option value="Normaal">Normaal</option>
          <option value="Hoog">Hoog</option>
          <option value="Spoed">Spoed</option>
        </select>
      </div>

      {/* Vaste header rij (alleen desktop) */}
      {werkorders.length > 0 && (
        <div ref={tableHeaderRef} className="desktop-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Klant</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Datum</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Onderwerp</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Prioriteit</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
          <div style={{ flex: '1.2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Klant</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Datum</div>
          <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem' }}>Onderwerp</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Prioriteit</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Totaal</div>
          <div style={{ flex: '1.2', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Desktop Werkorders lijst */}
      {werkorders.length === 0 ? (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen werkorders gevonden
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="werkorders-table-container">
            <div style={{
              background: 'white',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              {werkorders.map((werkorder) => (
                <div key={werkorder.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #dee2e6',
                  transition: 'background 0.2s',
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/werkorders/${werkorder.id}`)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ flex: '0.8', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                    {werkorder.relatie?.bedrijfsnaam === 'Particulier'
                      ? `${werkorder.relatie?.voornaam || ''} ${werkorder.relatie?.tussenvoegsel || ''} ${werkorder.relatie?.achternaam || ''}`.trim()
                      : werkorder.relatie?.bedrijfsnaam || '-'}
                  </div>
                  <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                    {formatDate(werkorder.werkorder_datum)}
                  </div>
                  <div style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                    {werkorder.onderwerp}
                  </div>
                  <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        backgroundColor: werkordersService.getStatusColor(werkorder.status),
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '85px',
                        height: '26px',
                        padding: '0 12px',
                        borderRadius: '13px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'white',
                        textTransform: 'uppercase'
                      }}
                    >
                      {werkorder.status}
                    </span>
                  </div>
                  <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        backgroundColor: werkordersService.getPrioriteitColor(werkorder.prioriteit),
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '85px',
                        height: '26px',
                        padding: '0 12px',
                        borderRadius: '13px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'white',
                        textTransform: 'uppercase'
                      }}
                    >
                      {werkorder.prioriteit}
                    </span>
                  </div>
                  <div style={{ flex: '0.7', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatCurrency(werkorder.totaal)}
                  </div>
                  <div className="actions" style={{ flex: '1.2', minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/werkorders/${werkorder.id}`)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      Openen
                    </button>
                    {hasPermission('werkorders_wijzigen') && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate(`/werkorders/${werkorder.id}/edit`)}
                        style={{ whiteSpace: 'nowrap', marginLeft: '0.5rem' }}
                      >
                        Bewerken
                      </button>
                    )}
                    {werkorder.offerte_id && hasPermission('offertes_bekijken') && (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => navigate(`/offertes/${werkorder.offerte_id}`)}
                        style={{ whiteSpace: 'nowrap', marginLeft: '0.5rem' }}
                      >
                        Offerte
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="mobile-werkorder-cards">
            {werkorders.map((werkorder) => (
              <div 
                key={werkorder.id} 
                className="mobile-werkorder-card"
                onClick={() => navigate(`/werkorders/${werkorder.id}`)}
              >
                <div className="mobile-card-header">
                  <h3 className="mobile-card-title">
                    {werkorder.relatie?.bedrijfsnaam === 'Particulier'
                      ? `${werkorder.relatie?.voornaam || ''} ${werkorder.relatie?.tussenvoegsel || ''} ${werkorder.relatie?.achternaam || ''}`.trim()
                      : werkorder.relatie?.bedrijfsnaam || '-'}
                  </h3>
                  <span className="mobile-card-number">WO-{werkorder.id}</span>
                </div>
                
                <div className="mobile-card-onderwerp">
                  {werkorder.onderwerp}
                </div>

                <div className="mobile-card-details">
                  <div className="mobile-card-detail-item">
                    <span className="mobile-card-detail-label">Datum</span>
                    <span className="mobile-card-detail-value">{formatDate(werkorder.werkorder_datum)}</span>
                  </div>
                  <div className="mobile-card-detail-item">
                    <span className="mobile-card-detail-label">Totaal</span>
                    <span className="mobile-card-detail-value">{formatCurrency(werkorder.totaal)}</span>
                  </div>
                </div>

                <div className="mobile-card-badges">
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: werkordersService.getStatusColor(werkorder.status)
                    }}
                  >
                    {werkorder.status}
                  </span>
                  <span
                    className="priority-badge"
                    style={{
                      backgroundColor: werkordersService.getPrioriteitColor(werkorder.prioriteit)
                    }}
                  >
                    {werkorder.prioriteit}
                  </span>
                </div>

                <div className="mobile-card-footer">
                  <div></div>
                  <div className="mobile-card-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => navigate(`/werkorders/${werkorder.id}`)}
                    >
                      Openen
                    </button>
                    {hasPermission('werkorders_wijzigen') && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigate(`/werkorders/${werkorder.id}/edit`)}
                      >
                        Bewerken
                      </button>
                    )}
                    {werkorder.offerte_id && hasPermission('offertes_bekijken') && (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => navigate(`/offertes/${werkorder.offerte_id}`)}
                      >
                        Offerte
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {werkorders.length > 0 && (
        <div className="table-footer">
          <p>{werkorders.length} werkorder(s) gevonden</p>
        </div>
      )}
    </div>
  );
};

export default WerkordersList;
